import { Button } from '@heidi/design-system/button';
import { Textarea } from '@heidi/design-system/textarea';
import { cn } from '@heidi/utils';
import { ArrowUp, AudioLines, Mic, MicOff, NotepadText } from 'lucide-react';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { voiceAssistantMessages as messages } from './voice-assistant.messages';
import { VoiceOrb } from './VoiceOrb';

/**
 * Shared composer row height — matches the size-9 mute / send chrome so
 * speech ↔ typing never remount into a different vertical box.
 */
const COMPOSER_ROW = 'h-9';

interface VoiceAssistantComposerProps {
  draft: string;
  isActive: boolean;
  muted: boolean;
  assistantSpeaking: boolean;
  micStream: MediaStream | null;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  onToggleMute: () => void;
  /** When omitted, the Start-dictation control stays hidden (flag off). */
  onStartDictation?: () => void;
  /** @deprecated End lives on the dock header; kept optional for call-site compat. */
  onEnd?: () => void;
  /** When true, drop outer shadow/border — parent dock already provides chrome. */
  embedded?: boolean;
}

/**
 * One composer shell, two middle skins. Speech: mute · visualiser (hover
 * Type → click opens typing). Typing: mute · fixed-height input · send /
 * back-to-voice. Row height is locked to the size-9 controls so the dock
 * does not jump when morphing. Speech mode has no trailing send button —
 * typing is opened via the visualiser.
 */
export function VoiceAssistantComposer({
  draft,
  isActive,
  muted,
  assistantSpeaking,
  micStream,
  onDraftChange,
  onSend,
  onToggleMute,
  onStartDictation,
  embedded = false,
}: VoiceAssistantComposerProps) {
  const intl = useIntl();
  const [speechMode, setSpeechMode] = useState(true);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!isActive) setSpeechMode(true);
  }, [isActive]);

  useEffect(() => {
    if (!speechMode) {
      // Enter typing → focus so the empty shell doesn't feel inert.
      inputRef.current?.focus();
    }
  }, [speechMode]);

  const submitText = () => {
    if (!draft.trim()) return;
    onSend();
    setSpeechMode(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitText();
    }
  };

  const enterSpeechMode = () => {
    if (muted) onToggleMute();
    setSpeechMode(true);
  };

  const exitSpeechMode = () => {
    setSpeechMode(false);
  };

  const shellClass = cn(
    'flex items-center gap-2',
    COMPOSER_ROW,
    embedded
      ? 'w-full'
      : 'w-[min(28rem,calc(100vw-2rem))] rounded-2xl border border-border/60 bg-surface px-1.5 shadow-lg'
  );

  // Mic = live / click to mute; MicOff = muted / click to unmute. Keep the
  // control always painted (even while connecting) so the Voice window chrome
  // never looks like it is missing a mute affordance.
  const muteControl = (
    <Button
      variant={muted ? 'destructive_soft' : 'ghost'}
      size="sm"
      iconOnly
      aria-label={
        muted ? intl.formatMessage(messages.unmute) : intl.formatMessage(messages.mute)
      }
      title={
        muted ? intl.formatMessage(messages.unmute) : intl.formatMessage(messages.mute)
      }
      onClick={onToggleMute}
      disabled={!isActive}
      className={cn(
        'size-9 shrink-0 rounded-full',
        muted && 'text-validation-destructive'
      )}
      data-testid="voice-assistant-mute"
    >
      {muted ? <MicOff className="size-4" /> : <Mic className="size-4" />}
    </Button>
  );

  const dictateControl = onStartDictation ? (
    <Button
      variant="ghost"
      size="sm"
      iconOnly
      aria-label={intl.formatMessage(messages.startDictation)}
      onClick={onStartDictation}
      disabled={!isActive}
      className="size-9 shrink-0 rounded-full"
      data-testid="voice-assistant-start-dictation"
    >
      <NotepadText className="size-4" />
    </Button>
  ) : null;

  if (speechMode) {
    return (
      <div className={shellClass} data-testid="voice-assistant-speech-pill">
        {muteControl}
        <button
          type="button"
          className={cn(
            'group relative flex h-full min-w-0 flex-1 cursor-pointer items-center rounded-xl px-2',
            'outline-none focus-visible:ring-2 focus-visible:ring-border-focus'
          )}
          aria-label={intl.formatMessage(messages.exitSpeechMode)}
          onClick={exitSpeechMode}
          data-testid="voice-assistant-open-typing"
        >
          <div className="w-full transition-opacity duration-150 group-hover:opacity-0 group-focus-visible:opacity-0">
            <VoiceOrb
              stream={micStream}
              active={(isActive && !muted) || assistantSpeaking}
            />
          </div>
          <span
            className={cn(
              'pointer-events-none absolute inset-0 flex items-center justify-center',
              'text-xs font-medium text-text-secondary',
              'opacity-0 transition-opacity duration-150',
              'group-hover:opacity-100 group-focus-visible:opacity-100'
            )}
            aria-hidden
          >
            {intl.formatMessage(messages.typeHint)}
          </span>
        </button>
        {dictateControl}
      </div>
    );
  }

  return (
    <div className={shellClass} data-testid="voice-assistant-text-pill">
      {muteControl}
      <Textarea
        ref={inputRef}
        rows={1}
        value={draft}
        onChange={(event) => onDraftChange(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={intl.formatMessage(messages.textPlaceholder)}
        disabled={!isActive}
        aria-label={intl.formatMessage(messages.textPlaceholder)}
        data-testid="voice-assistant-text-input"
        // Locked to COMPOSER_ROW — no autoResize (avoids measure-settle jump).
        className={cn(
          'min-w-0 flex-1 resize-none overflow-y-auto border-0 bg-transparent px-1',
          'shadow-none focus-visible:ring-0',
          COMPOSER_ROW,
          'min-h-9 py-0 text-sm leading-9'
        )}
      />
      {draft.trim() ? (
        <Button
          variant="default"
          size="sm"
          iconOnly
          aria-label={intl.formatMessage(messages.sendText)}
          onClick={submitText}
          disabled={!isActive}
          className="size-9 shrink-0 rounded-full"
          data-testid="voice-assistant-send-text"
        >
          <ArrowUp className="size-4" />
        </Button>
      ) : (
        <Button
          variant="default"
          size="sm"
          iconOnly
          aria-label={intl.formatMessage(messages.enterSpeechMode)}
          onClick={enterSpeechMode}
          disabled={!isActive}
          className="size-9 shrink-0 rounded-full"
          data-testid="voice-assistant-enter-speech"
        >
          <AudioLines className="size-4" />
        </Button>
      )}
    </div>
  );
}
